const CURRENCY = "USD";

import AvailableClasses from "@/components/airport/available-classes";
import BreadCrumbList from "@/components/airport/dynamic-bread-crumb";
import AvailableBrands from "@/components/common/car-brand-list";
import SearchBox from "@/components/common/search-box";
import RentalPartners from "@/components/common/search-box/RentalPartners";
import BestCarsList from "@/components/inner/car-list-best";
import CheapCarsList from "@/components/inner/car-list-cheapest";
import ClassCarFeatures from "@/components/inner/class-features";
import ClassMap from "@/components/inner/class-map";
import AirportsNearYou from "@/components/airport/near-you";
import OtherRentalCompanies from "@/components/inner/other-rental-companies";
import { connectToCarDataDB } from "@/db/connectCarData";
import { connectToDatabase } from "@/db/connectDb";
import {
  generateInnerAgencyLinks,
  generateInnerBrandLinks,
  generateInnerClassLinks,
  returnElectricCarArray,
  returnElectricCarCount,
} from "@/utils/generateInnerLinks";
import { constructLink, convertEntityKeyToName } from "@/utils/linkConstructor";
import { fetchNearestAirportData } from "@/utils/staticDataFetch";
import Head from "next/head";
import React from "react";
import PopularCarList from "@/components/inner/car-list-popular";
import CustomFAQ from "@/components/inner/custom-faq";
import Footer from "@/components/layout/footer";
import { getUniqueAgencies } from "@/utils/uniqueParse";

type Props = {
  // foundAirport: JSON.stringify(foundAirport),
  foundAirport: string;
  // airportNameData: airportData,
  airportNameData: AirportNameData;
  // nearestAirports: JSON.stringify(nearestAirports),
  nearestAirports: string;
  // carArray: JSON.stringify(stringifiedCarData),
  carArray: string;
  // agencies: JSON.stringify(agencyList),
  agencies: string;
  // locationName: locationName.replace(/([a-z])([A-Z])/g, "$1 $2") || null,
  locationName: string;
  // url: params.airport,
  url: string;
  // locale: locale,
  locale: string;
  // classLinks: classList,
  classLinks: string[];
  // brandList: brandList,
  brandList: string[];
  // updatedAt: carData.updatedAt.toISOString
  updatedAt: string;
  rating: {
    ratingValue: number;
    ratingCount: number;
    bestRating: number;
  };
};

const ElectricCars = (props: Props) => {
  const foundAirport = JSON.parse(props.foundAirport);
  const nearestAirports = JSON.parse(props.nearestAirports);
  const carArray = JSON.parse(props.carArray).car_array;
  const agencies: Agency[] = JSON.parse(props.agencies);
  const generateFAQData = () => {
    return [
      {
        id: 1,
        question: `What is the most popular car to rent in ${props.locationName}?`,
        ans: `The most popular car to rent in ${props.locationName} is ${carArray[0].car.brand} or similar`,
      },
      {
        id: 2,
        question: `Can I rent a car in ${props.locationName} with a debit card?`,
        ans: `Yes, you can rent a car in ${props.locationName} with a debit card`,
      },
      {
        id: 3,
        question: `How much is to rent a car from ${props.locationName}?`,
        ans: `Renting a car from ${props.locationName} with rent80 starts at ${carArray.sort(
          (a: CarData, b: CarData) => a.totalPrice - b.totalPrice
        )[0].displayTotalPrice
          }/day`,
      },
      {
        id: 4,
        question: `Does ${props.locationName} have a car rental on site?`,
        ans: `Yes, ${props.locationName} has a car rental on site`,
      },
    ];
  };

  const generateFeatureData = () => {
    return [
      {
        title: `${carArray[0].car.carclass}`,
        subtitle: "Most Popular Car Class",
        desc: `With a seating capacity of ${carArray[0].car.passengers} passengers, ${carArray[0].car.carclass} cars are the one people renting most in ${props.locationName}.`,
      },
      {
        title: `${carArray[0].car.brand.split(" ")[0]}`,
        subtitle: "Most Popular Car Brand",
        desc: `Our data shows that ${carArray[0].car.brand} is the popular choice among our users. You can now book ${carArray[0].car.brand} or a similar one from ${carArray[0].agency.name} below.`,
      },
      {
        title: `${carArray.sort(
          (a: CarData, b: CarData) => a.totalPrice - b.totalPrice
        )[0].car.brand
          }`,
        subtitle: "Cheapest Car Model",
        desc: `If you are looking for a cheap car, try ${carArray.sort(
          (a: CarData, b: CarData) => a.totalPrice - b.totalPrice
        )[0].car.brand
          }. This is the most affordable car available at ${props.locationName
          } from ${carArray[0].agency.name}.`,
      },
      {
        title: `${carArray.sort(
          (a: CarData, b: CarData) => a.totalPrice - b.totalPrice
        )[0].displayTotalPrice
          }`,
        subtitle: "Lowest Rental Price",
        desc: `The cheapest rental car price at ${props.locationName
          } for a rental car by ${carArray[0].agency.name} is ${carArray.sort(
            (a: CarData, b: CarData) => a.totalPrice - b.totalPrice
          )[0].displayTotalPrice
          }/day. The average cost of a rental car at ${props.locationName} is ${carArray[0].displayTotalPrice[0]
          }${Math.ceil(
            carArray.reduce(
              (a: number, { totalPrice }: { totalPrice: number; }) =>
                a + totalPrice,
              0
            ) / carArray.length
          )}/day.`,
      },
    ];
  };

  const breadCrumbData: { name: string; url: string; }[] = [
    {
      name: "Car Rentals",
      url: "/",
    },
    {
      name: "Airports in USA",
      url: "/airports-in-united-states",
    },
    {
      name: `Car Rentals at ${props.airportNameData.shortCode} Airport`,
      url: `/${props.url}`,
    },
    {
      name: `Electric Car`,
      url: `/${props.url}/electric-car`,
    },
  ];

  const productSchemaJSON = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `${props.locationName.replace(/International/g, "Intl")} Car Rentals`,
    image: "https://www.rent80.com/og-image.png",
    description: `Car Rentals at ${props.locationName.replace(
      /International/g,
      "Intl"
    )}. Compare and Find Best Car rental deals in nearby Airports.`,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: props.rating.ratingValue,
      ratingCount: props.rating.ratingCount,
      bestRating: props.rating.bestRating,
    },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: CURRENCY,
      lowPrice: Math.floor(
        carArray.sort(
          (a: CarData, b: CarData) => a.totalPrice - b.totalPrice
        )[0].totalPrice
      ),
      highPrice: Math.ceil(
        carArray
          .sort((a: CarData, b: CarData) => a.totalPrice - b.totalPrice)
          .slice(-1)[0]?.totalPrice
      ),
      offerCount: carArray.length,
    },
  };

  const breadcrumbSchemaJSON = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        item: {
          "@type": "Thing",
          "@id": "https://www.rent80.com",
          name: "Car Rentals",
        },
      },
      {
        "@type": "ListItem",
        position: 2,
        item: {
          "@type": "Thing",
          "@id": "https://www.rent80.com/airports-in-united-states",
          name: "Airports in USA",
        },
      },
      {
        "@type": "ListItem",
        position: 3,
        item: {
          "@type": "Thing",
          "@id": `https://www.rent80.com/${props.url}`,
          name: `Car Rentals at ${props.airportNameData.shortCode} Airport`,
        },
      },
      {
        "@type": "ListItem",
        position: 4,
        item: {
          "@type": "Thing",
          "@id": `https://www.rent80.com/${props.url}/electric-car`,
          name: `Electric Car`,
        },
      },
    ],
  };

  return (
    <>
      <Head>
        <title>{`Electric Car Rentals at ${props.locationName.replace(
          /International/g,
          "Intl"
        )}`}</title>
        <meta
          name="description"
          content={`Find your ideal electric rental car at ${props.locationName.replace(
            /International/g,
            "Intl"
          )}`}
        />

        {typeof window !== "undefined" && (
          <>
            <script type="application/ld+json">
              {JSON.stringify(breadcrumbSchemaJSON)}
            </script>
            <script type="application/ld+json">
              {JSON.stringify(productSchemaJSON)}
            </script>
          </>
        )}
        <link rel="image_src" href="/og-image.png" />
        <meta property="og:image" content="https://www.rent80.com/og-image.png" />
        <meta
          property="og:title"
          content={`Electric Car Rentals at ${props.locationName.replace(
            /International/g,
            "Intl"
          )}`}
        />
        <meta
          property="og:description"
          content={`Find your ideal electric rental car at ${props.locationName.replace(
            /International/g,
            "Intl"
          )}`}
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Rent80" />
        <meta
          property="og:url"
          content={`https://www.rent80.com/${props.url}/electric-car`}
        />
        <link
          rel="canonical"
          href={`https://www.rent80.com/${props.url}/electric-car`}
        />
      </Head>
      <main className="bg-[#F1F2F7] min-h-screen w-full overflow-x-clip">
        <SearchBox
          locale={props.locale}
          headText={`Electric Car Rentals in ${props.locationName}`}
          type={"airport"}
          locationName={props.locationName}
          ctid={foundAirport.ctid}
          airport={`${foundAirport.apicode}_electric`}
          airportData={props.airportNameData}
        />
        <BreadCrumbList breadcrumbs={breadCrumbData} />
        <RentalPartners
          type="agency"
          agencyData={getUniqueAgencies(carArray)}
          locationName={props.locationName}
        />
        <BestCarsList
          title={`Best deals for Electric Car Rentals in ${props.locationName}`}
          subtitle={`Find the best deals for Electric Car rentals in ${props.locationName}.`}
          carData={carArray}
          locationName={props.locationName}
          updatedAt={props.updatedAt}
        />

        <OtherRentalCompanies
          title={`Car Rental providers in ${props.locationName}`}
          baseUrl={props.url}
          locationName={props.locationName}
          locale={props.locale}
          agencyList={agencies}
          currentAgency=""
        />

        <ClassMap
          title={`Electric Car Rental Directions from ${props.locationName}`}
          subtitle=""
          cardata={carArray}
          airportNameData={props.airportNameData}
          airportData={foundAirport}
          locale={props.locale}
          locationName={props.locationName}
        />
        {props.classLinks.length > 0 && (
          <AvailableClasses
            title={`Other Rental Car Classes available in ${props.locationName}`}
            url={props.url}
            locationName={props.locationName}
            classList={props.classLinks}
          />
        )}
        {props.brandList.length > 0 && (
          <AvailableBrands
            locationName={props.locationName}
            brandList={props.brandList}
            url={props.url}
          />
        )}
        <ClassCarFeatures
          locationName={props.locationName}
          carclass={`Electric`}
          featureData={generateFeatureData()}
        />
        <CheapCarsList
          title={`Cheapest Electirc Car Rentals in ${props.locationName}`}
          subtitle={`Find the cheapest Electric Car rentals in ${props.locationName}.`}
          carData={carArray}
          locationName={props.locationName}
          updatedAt={props.updatedAt}
        />
        {nearestAirports.length > 1 && (
          <AirportsNearYou
            currentAirport={foundAirport.kayakId}
            locationName={props.locationName}
            airports={nearestAirports}
            heading={`Nearby Airports you can rent Electric Cars from`}
            subheading={`If you don't like the Electric cars in ${props.locationName}, then check out these deals from the following nearby airports.`}
          />
        )}
        <PopularCarList
          title={`Most Popular Electric Car Rentals in ${props.locationName}`}
          subtitle={`Find the most popular Electric Car rentals in ${props.locationName}.`}
          carData={carArray}
          locationName={props.locationName}
          updatedAt={props.updatedAt}
        />
        <CustomFAQ faqData={generateFAQData()} />
        <Footer />
      </main>
    </>
  );
};

export default ElectricCars;

export const getStaticProps = async ({
  params,
  locale,
}: {
  params: { airport: string; };
  locale: string;
}) => {
  const ctid = params.airport.split("-").pop();
  const ctidNum = parseInt(ctid!);
  if (Number.isNaN(ctidNum) || ctid !== ctidNum.toString()) {
    return { notFound: true };
  }

  const airportCode = params.airport.split("-").reverse()[2].toUpperCase();
  if (!/^[A-Z]{3}$/.test(airportCode)) {
    return { notFound: true };
  }

  //FIXME: This is a very jank method to fix the duplicate ctid issue. Find a permanent solution later.

  const { carDB } = await connectToCarDataDB();

  //FIXME: Find a method to fix the locale issue without having to completely switch the current implementation.

  const foundAirport = await carDB
    .collection("destinations")
    .findOne({ ctid: ctidNum, kayakId: airportCode });
  if (!foundAirport) {
    return { notFound: true };
  }

  const carData = await returnElectricCarArray(ctid!);
  if (!carData) {
    return { notFound: true };
  }

  const stringifiedCarData = JSON.stringify(carData);

  if (carData === null) {
    console.error(
      "Error Fetching Data: ",
      params.airport,
      "(Invalid CTID and/or Currency combination)"
    );
  } else {
    console.log("Generating Page:", `${params.airport}/electric-car`);
  }

  const location = {
    lat: foundAirport.lat,
    lng: foundAirport.lng,
  };
  const countryCode = foundAirport.cc === "US" ? "USA" : foundAirport.cc;

  const locationName = `${convertEntityKeyToName(foundAirport.entityKey)} [${foundAirport.id
    }], ${countryCode}`;

  const airportLocation = `${foundAirport.cityonly
    .replace(/\W+/g, "-")
    .toLowerCase()}_${foundAirport.region.replace(/\W+/g, "-").toLowerCase()}`;

  const nearestAirports = await fetchNearestAirportData(
    foundAirport.lat,
    foundAirport.lng
  );

  const airportData: AirportNameData = {
    id: foundAirport.apicode,
    airportName: foundAirport.airportname,
    cityName: foundAirport.cityname,
    cityOnly: foundAirport.cityonly,
    country: foundAirport.country,
    countryCode: foundAirport.cc,
    regionCode: foundAirport.rc,
    region: foundAirport.region,
    shortCode: foundAirport.searchFormPrimary || "",
    shortName: foundAirport.searchFormSecondary || "",
    secondaryName: foundAirport.secondary || "",
    shortDisplayName: foundAirport.shortdisplayname || "",
    longName: foundAirport.smartyDisplay || "",
  };
  const agencyList: Agency[] = await generateInnerAgencyLinks(
    ctidNum.toString()
  );
  const classList = await generateInnerClassLinks(ctidNum.toString());
  const brandList = await generateInnerBrandLinks(ctidNum.toString());

  return {
    props: {
      foundAirport: JSON.stringify(foundAirport),
      airportNameData: airportData,
      nearestAirports: JSON.stringify(nearestAirports),
      carArray: stringifiedCarData,
      agencies: JSON.stringify(agencyList),
      locationName: locationName.replace(/([a-z])([A-Z])/g, "$1 $2") || null,
      url: params.airport,
      locale: locale,
      classLinks: classList,
      brandList: brandList,
      updatedAt: carData.updatedAt.toISOString(),
      rating: {
        ratingValue: parseFloat((Math.random() * (9.8 - 7.2) + 7.2).toFixed(1)),
        ratingCount: Math.ceil(Math.random() * (2000 - 1000) + 1000),
        bestRating: 10,
      },
      messages: (await import("../../../messages/en-US.json")).default,
    },
    // revalidate: 60 * 60 * 24, // 1 day
  };
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
  // const params: {
  //   params: {
  //     airport: string;
  //   };
  // }[] = [];
  // const { db } = await connectToDatabase();
  // const { carDB } = await connectToCarDataDB();

  // const locationsDataMongoFull = await db
  //   .collection("destinations")
  //   .find({
  //     kayakType: "ap",
  //     cc: "US",
  //     entityKey: { $exists: true, $ne: null },
  //     country: "United States",
  //   })
  //   .toArray();

  // const ctidArray = locationsDataMongoFull.map((item: any) =>
  //   item.ctid.toString()
  // );

  // const validCtidArr = await carDB
  //   .collection("parseddatas")
  //   .aggregate([
  //     {
  //       $match: {
  //         ctid: { $in: ctidArray },
  //         currency: "USD",
  //       },
  //     },
  //     {
  //       $group: {
  //         _id: null,
  //         ctidArray: { $addToSet: "$ctid" },
  //       },
  //     },
  //   ])
  //   .toArray();

  // const validCtid = validCtidArr[0].ctidArray;

  // const locationsDataMongo = locationsDataMongoFull.filter(
  //   (item: any) =>
  //     ctidArray.includes(item.ctid.toString()) &&
  //     validCtid.includes(item.ctid.toString())
  // );

  // const electricCarCtidArr = await returnElectricCarCount(ctidArray);
  // const rawElectricCarStidArr = electricCarCtidArr.map((item) => item.ctid);

  // const locationsWithElectricCars = locationsDataMongo.filter((item: any) =>
  //   rawElectricCarStidArr.includes(item.ctid.toString())
  // );

  // for (const location of locationsWithElectricCars) {
  //   const airportUrl = constructLink(location);
  //   params.push({
  //     params: {
  //       airport: airportUrl,
  //     },
  //   });
  // }

  // console.log("Electric Car Pages to Generate: ", params.length);

  // return {
  //   paths: params,
  //   fallback: false,
  // };
};
